/**
 * Playmaker — Wedge expanded-data right-side drawer.
 * Slides in from the right, ~480px wide. One drawer, per-provider content.
 *
 * Triggered by Wedge component when secondary action is `expand:{provider}`.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtMoney } from "../../lib/format";
import {
  X, ExternalLink, AlertCircle, TrendingUp, TrendingDown,
  Sparkles, ArrowRight, Calendar as CalendarIcon,
} from "lucide-react";

const PERC_TONE = {
  green: "bg-emerald-500",
  blue: "bg-blue-500",
  amber: "bg-amber-500",
  gray: "bg-slate-400",
};

export function WedgeDrawer({ open, provider, clientId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [compareId, setCompareId] = useState(null); // SciSports compare-mode
  const [compareData, setCompareData] = useState(null);

  useEffect(() => {
    if (!open || !provider || !clientId) return;
    setLoading(true); setErr(null); setData(null); setCompareId(null); setCompareData(null);
    api.get(`/wedges/${provider}/${clientId}/full`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load expanded view"))
      .finally(() => setLoading(false));
  }, [open, provider, clientId]);

  // SciSports compare lookup
  useEffect(() => {
    if (provider !== "scisports" || !compareId) { setCompareData(null); return; }
    api.get(`/wedges/scisports/${compareId}/full`)
      .then((r) => setCompareData(r.data)).catch(() => setCompareData(null));
  }, [compareId, provider]);

  if (!open) return null;

  return (
    <>
      {/* Subtle backdrop — pointer-events-none so it doesn't intercept clicks on the rest of the page.
          Drawer is a panel (per spec), not a modal — agent can switch tabs while it's open. */}
      <div className="fixed inset-y-0 left-0 right-[480px] z-40 bg-slate-900/20 backdrop-blur-[1px] pointer-events-none" data-testid="wedge-drawer-overlay" />
      <aside
        className="fixed top-0 right-0 z-50 h-full w-full max-w-[480px] bg-white shadow-2xl border-l border-slate-200 overflow-y-auto"
        data-testid={`wedge-drawer-${provider}`}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start gap-3 z-10">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{data?.subtitle || "Loading…"}</div>
            <div className="font-display text-lg font-bold tracking-tight truncate">{data?.title || "Full data"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1 rounded-md hover:bg-slate-100" data-testid="wedge-drawer-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          {loading && <div className="text-sm text-slate-500">Loading expanded view…</div>}
          {err && (
            <div className="text-sm text-red-700 flex items-center gap-1.5"><AlertCircle size={12} /> {err}</div>
          )}
          {data && data.stub && (
            <div className="mb-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
              Stub data — connect a real account to see live numbers.
            </div>
          )}

          {data && provider === "wyscout" && <WyscoutFull data={data} />}
          {data && provider === "scisports" && (
            <SciSportsFull data={data} compareId={compareId} setCompareId={setCompareId} compareData={compareData} />
          )}
          {data && provider === "transfermarkt" && <TransfermarktFull data={data} />}
          {data && provider === "transferroom" && <TransferRoomFull data={data} />}
          {data && provider === "api_football" && <ApiFootballFull data={data} />}
        </div>
      </aside>
    </>
  );
}

/* -------------------- Wyscout: season table + last 10 matches -------------------- */

function WyscoutFull({ data }) {
  return (
    <div className="space-y-5">
      <section>
        <h3 className="font-display text-sm font-black tracking-tight mb-2 uppercase text-slate-500 tracking-[0.22em]">Season totals</h3>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-[11px]" data-testid="wyscout-seasons-table">
            <thead>
              <tr className="text-slate-400 uppercase tracking-[0.16em] text-[9px] font-bold">
                <th className="text-left p-1.5">Season</th><th className="text-left p-1.5">Comp</th>
                <th className="text-right p-1.5">App</th><th className="text-right p-1.5">Min</th>
                <th className="text-right p-1.5">G</th><th className="text-right p-1.5">A</th>
                <th className="text-right p-1.5">xG</th><th className="text-right p-1.5">xA</th>
                <th className="text-right p-1.5">★</th>
              </tr>
            </thead>
            <tbody>
              {data.seasons.map((s, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`wyscout-season-${i}`}>
                  <td className="p-1.5 font-bold">{s.label}</td>
                  <td className="p-1.5 text-slate-600">{s.competition}</td>
                  <td className="p-1.5 text-right font-mono-tab">{s.appearances}</td>
                  <td className="p-1.5 text-right font-mono-tab">{s.minutes}</td>
                  <td className="p-1.5 text-right font-mono-tab font-bold text-emerald-700">{s.goals}</td>
                  <td className="p-1.5 text-right font-mono-tab font-bold text-blue-700">{s.assists}</td>
                  <td className="p-1.5 text-right font-mono-tab">{s.xg}</td>
                  <td className="p-1.5 text-right font-mono-tab">{s.xa}</td>
                  <td className="p-1.5 text-right font-mono-tab font-bold">{s.rating}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3 className="font-display text-sm font-black tracking-tight mb-2 uppercase text-slate-500 tracking-[0.22em]">Last 10 matches</h3>
        <ul className="space-y-1.5" data-testid="wyscout-last-matches">
          {data.last_matches.map((m, i) => (
            <li key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors text-[12px]">
              <span className="font-mono-tab text-slate-500 w-16 shrink-0">{m.date.slice(5)}</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 w-7 shrink-0">{m.home ? "H" : "A"}</span>
              <span className="flex-1 min-w-0 truncate font-bold">{m.opp}</span>
              <span className="text-[10px] text-slate-500 truncate hidden sm:inline">{m.competition}</span>
              <span className="font-mono-tab text-slate-500 w-7 text-right">{m.minutes}'</span>
              <span className="font-mono-tab text-emerald-700 font-bold w-5 text-right">{m.goals}G</span>
              <span className="font-mono-tab text-blue-700 font-bold w-5 text-right">{m.assists}A</span>
              <span className="font-mono-tab font-bold w-8 text-right">★{m.rating}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/* -------------------- SciSports: full radar + compare mode -------------------- */

function SciSportsFull({ data, compareId, setCompareId, compareData }) {
  const maxAttrs = data.radar.length;
  return (
    <div className="space-y-5">
      <div className="flex items-baseline gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">SciSkill Index</div>
          <div className="font-display text-3xl font-black tracking-tight text-rose-700">{data.sciskill_index?.toFixed(1)}</div>
        </div>
        <span className={`pm-pill text-[10px] ${data.trend === "improving" ? "pm-pill-green" : data.trend === "declining" ? "pm-pill-red" : "pm-pill-slate"}`}>
          {data.trend === "improving" ? "↑ Improving" : data.trend === "declining" ? "↓ Declining" : "→ Stable"}
        </span>
        <div className="flex-1" />
        <span className="text-[11px] text-slate-500 italic">{data.role}</span>
      </div>

      {/* Full radar as percentile bars */}
      <section>
        <h3 className="font-display text-sm font-black tracking-tight mb-2 uppercase text-slate-500 tracking-[0.22em]">
          Full radar · {maxAttrs} attributes
        </h3>
        <div className="space-y-2" data-testid="scisports-full-radar">
          {data.radar.map((r, i) => {
            const peerPct = compareData?.radar?.[i]?.percentile;
            return (
              <div key={r.attribute} className="text-[12px]">
                <div className="flex items-baseline justify-between mb-0.5">
                  <span className="font-bold text-slate-700">{r.attribute}</span>
                  <span className="font-mono-tab font-bold">
                    {r.percentile}th
                    {peerPct != null && <span className="text-rose-500 ml-2">vs {peerPct}th</span>}
                  </span>
                </div>
                <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`absolute inset-y-0 left-0 ${PERC_TONE[r.tone] || "bg-slate-400"}`} style={{ width: `${r.percentile}%` }} />
                  {peerPct != null && (
                    <div className="absolute top-0 bottom-0 w-0.5 bg-rose-600" style={{ left: `${peerPct}%` }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="font-display text-sm font-black tracking-tight mb-2 uppercase text-slate-500 tracking-[0.22em]">Compare mode</h3>
        {data.comparison_candidates?.length > 0 ? (
          <select
            className="pm-input"
            value={compareId || ""}
            onChange={(e) => setCompareId(e.target.value || null)}
            data-testid="scisports-compare-select"
          >
            <option value="">— Pick a peer to overlay —</option>
            {data.comparison_candidates.map((p) => (
              <option key={p.id} value={p.id}>{p.full_name}{p.club ? ` · ${p.club}` : ""}</option>
            ))}
          </select>
        ) : (
          <select
            className="pm-input opacity-60"
            disabled
            value=""
            data-testid="scisports-compare-select"
          >
            <option value="">No peers in same position yet</option>
          </select>
        )}
        {compareData && (
          <div className="mt-2 text-[11px] text-slate-600">
            Comparing against <b>{compareData.subtitle}</b> · SciSkill <b>{compareData.sciskill_index?.toFixed(1)}</b>
          </div>
        )}
      </section>
    </div>
  );
}

/* -------------------- Transfermarkt: full chart with labels + transfers -------------------- */

function TransfermarktFull({ data }) {
  const points = data.history || [];
  const w = 440, h = 200, pad = 30;
  const max = Math.max(...points.map((p) => p.value_eur));
  const min = Math.min(...points.map((p) => p.value_eur));
  const range = max - min || 1;
  const stride = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${pad + i * stride},${h - pad - ((p.value_eur - min) / range) * (h - pad * 2)}`)
    .join(" ");

  return (
    <div className="space-y-5">
      <div className="flex items-baseline gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Current value</div>
          <div className="font-display text-3xl font-black tracking-tight">{fmtMoney(data.current_value_eur)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Peak</div>
          <div className="font-display text-lg font-bold text-emerald-700">{fmtMoney(data.peak_value_eur)}</div>
        </div>
      </div>

      <section>
        <h3 className="font-display text-sm font-black tracking-tight mb-2 uppercase text-slate-500 tracking-[0.22em]">Value history</h3>
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="tm-full-chart">
          {/* Y-axis labels */}
          <text x={5} y={pad} fontSize="9" fill="#94a3b8" className="font-mono-tab">{fmtMoney(max)}</text>
          <text x={5} y={h - pad + 5} fontSize="9" fill="#94a3b8" className="font-mono-tab">{fmtMoney(min)}</text>
          {/* Grid line */}
          <line x1={pad} y1={(h - pad * 2) / 2 + pad} x2={w - pad} y2={(h - pad * 2) / 2 + pad} stroke="#f1f5f9" strokeDasharray="2,2" />
          {/* Path */}
          <path d={path} fill="none" stroke="#059669" strokeWidth="2.5" />
          {/* Dots + labels */}
          {points.map((p, i) => {
            const x = pad + i * stride;
            const y = h - pad - ((p.value_eur - min) / range) * (h - pad * 2);
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="3" fill="#059669" />
                <title>{p.date}: {fmtMoney(p.value_eur)}</title>
                {i === 0 || i === points.length - 1 ? (
                  <text x={x} y={h - 5} fontSize="9" fill="#64748b" textAnchor="middle" className="font-mono-tab">{p.date.slice(0, 7)}</text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </section>

      <section>
        <h3 className="font-display text-sm font-black tracking-tight mb-2 uppercase text-slate-500 tracking-[0.22em]">Transfer history</h3>
        <ul className="space-y-1.5" data-testid="tm-transfer-history">
          {data.transfers.map((t, i) => (
            <li key={i} className="flex items-center gap-2 text-[12px] p-2 rounded-lg bg-slate-50">
              <span className="font-mono-tab text-slate-500 w-16 shrink-0">{t.date}</span>
              <span className="text-slate-700">{t.from}</span>
              <ArrowRight size={11} className="text-slate-400" />
              <span className="font-bold flex-1 truncate">{t.to}</span>
              <span className="font-mono-tab font-bold text-emerald-700">{t.fee_eur ? fmtMoney(t.fee_eur) : "free"}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/* -------------------- TransferRoom expanded -------------------- */

function TransferRoomFull({ data }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">xTV</div>
        <div className="font-display text-3xl font-black tracking-tight">{fmtMoney(data.xtv_eur)}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-[12px]">
        <div className="p-2 rounded-lg bg-slate-50"><div className="text-[9px] uppercase tracking-widest text-slate-400">Ask price</div><div className="font-bold">{fmtMoney(data.ask_price_eur)}</div></div>
        <div className="p-2 rounded-lg bg-slate-50"><div className="text-[9px] uppercase tracking-widest text-slate-400">Availability</div><div className="font-bold">{data.availability}</div></div>
      </div>
      {data.salary_benchmark && <div className="text-[12px] text-slate-700">Benchmark: {data.salary_benchmark}</div>}
    </div>
  );
}

/* -------------------- API-Football full schedule -------------------- */

function ApiFootballFull({ data }) {
  if (!data.fixtures?.length) {
    return <div className="text-sm text-slate-500 italic">No fixtures on file.</div>;
  }
  return (
    <div>
      <h3 className="font-display text-sm font-black tracking-tight mb-2 uppercase text-slate-500 tracking-[0.22em]">{data.fixtures.length} fixtures</h3>
      <ul className="space-y-1.5" data-testid="api-football-full-list">
        {data.fixtures.map((f, i) => (
          <li key={f.id || i} className="flex items-center gap-2 text-[12px] p-2 rounded-lg hover:bg-slate-50 transition-colors">
            <CalendarIcon size={12} className="text-slate-400 shrink-0" />
            <span className="font-mono-tab text-slate-500 w-20 shrink-0">{f.kickoff && new Date(f.kickoff).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
            <span className="flex-1 min-w-0 truncate font-bold">{f.home_team} vs {f.away_team}</span>
            <span className="text-[10px] text-slate-500 truncate hidden sm:inline">{f.competition}</span>
            {f.score && <span className="font-mono-tab font-bold text-emerald-700">{f.score}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default WedgeDrawer;
